import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../../App';
import AdminLayout from '../../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { motion } from 'framer-motion';
import { 
  Users, 
  Shield, 
  List, 
  ArrowRight,
  UserPlus,
  Settings,
  Activity
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const { token, user } = useContext(AuthContext);
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    usersByRole: {}
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      // If endpoint doesn't exist yet, use defaults
      console.log('Admin stats endpoint not available');
    } finally {
      setLoading(false);
    }
  };

  const adminCards = [
    {
      title: 'User Management',
      description: 'Create, edit, and manage user accounts',
      icon: Users,
      color: 'text-blue-500',
      bgColor: 'bg-blue-50 hover:bg-blue-100',
      borderColor: 'border-blue-200',
      path: '/admin/users',
      testId: 'admin-card-users',
      stats: `${stats.totalUsers || 0} users`
    },
    {
      title: 'Roles & Permissions',
      description: 'View and understand role-based access control',
      icon: Shield,
      color: 'text-purple-500',
      bgColor: 'bg-purple-50 hover:bg-purple-100',
      borderColor: 'border-purple-200',
      path: '/admin/roles',
      testId: 'admin-card-roles',
      stats: '5 roles defined'
    },
    {
      title: 'Dropdown Configuration',
      description: 'Manage dropdown options for property forms',
      icon: List,
      color: 'text-emerald-500',
      bgColor: 'bg-emerald-50 hover:bg-emerald-100',
      borderColor: 'border-emerald-200',
      path: '/admin/lookups',
      testId: 'admin-card-lookups',
      stats: '8 lookup types'
    }
  ];

  // Check admin access
  const isAdmin = ['director', 'admin'].includes(user?.role);
  if (!isAdmin) {
    return (
      <AdminLayout>
        <div className="text-center py-12" data-testid="admin-access-denied">
          <Shield className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
          <h2 className="text-2xl font-bold text-primary mb-2">Access Denied</h2>
          <p className="text-muted-foreground mb-4">You need admin privileges to access this area.</p>
          <Button onClick={() => navigate('/dashboard')} data-testid="return-to-dashboard-btn">
            Return to Dashboard
          </Button>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-8" data-testid="admin-dashboard-page">
        {/* Header */}
        <div>
          <h1 className="text-4xl font-bold text-primary mb-2" data-testid="admin-dashboard-title">
            Admin Dashboard
          </h1>
          <p className="text-muted-foreground">
            Manage users, roles, and application settings
          </p>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100 text-sm">Total Users</p>
                  <p className="text-3xl font-bold">{stats.totalUsers || 0}</p>
                </div>
                <Users className="h-10 w-10 text-blue-200" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-emerald-100 text-sm">Active Users</p>
                  <p className="text-3xl font-bold">{stats.activeUsers || 0}</p>
                </div>
                <Activity className="h-10 w-10 text-emerald-200" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-100 text-sm">Admin Users</p>
                  <p className="text-3xl font-bold">
                    {(stats.usersByRole?.director || 0) + (stats.usersByRole?.admin || 0)}
                  </p>
                </div>
                <Shield className="h-10 w-10 text-purple-200" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Admin Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {adminCards.map((card, index) => (
            <motion.div
              key={card.path}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <Card
                className={`cursor-pointer transition-all duration-300 border-2 ${card.bgColor} ${card.borderColor} group`}
                onClick={() => navigate(card.path)}
                data-testid={card.testId}
              >
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <card.icon className={`h-10 w-10 ${card.color}`} />
                    <ArrowRight className="h-5 w-5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                  <CardTitle className="text-xl mt-4">{card.title}</CardTitle>
                  <CardDescription>{card.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <Badge variant="secondary" className="text-xs">
                    {card.stats}
                  </Badge>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5 text-amber-500" />
              Quick Actions
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Button
              variant="outline"
              className="h-auto py-4 justify-start"
              onClick={() => navigate('/admin/users?action=create')}
              data-testid="quick-action-add-user"
            >
              <UserPlus className="h-5 w-5 mr-3 text-blue-500" />
              <div className="text-left">
                <p className="font-medium">Add New User</p>
                <p className="text-xs text-muted-foreground">Create a new user account</p>
              </div>
            </Button>
            <Button
              variant="outline"
              className="h-auto py-4 justify-start"
              onClick={() => navigate('/admin/lookups')}
              data-testid="quick-action-manage-lookups"
            >
              <List className="h-5 w-5 mr-3 text-emerald-500" />
              <div className="text-left">
                <p className="font-medium">Manage Dropdowns</p>
                <p className="text-xs text-muted-foreground">Configure form options</p>
              </div>
            </Button>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
